import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ReactElement } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Layout } from '@/components/Layout'
import { LoginPage } from '@/pages/LoginPage'
import { SignupPage } from '@/pages/SignupPage'
import { Spinner } from '@/components/ui'
import { useIsInternalAdmin } from '@/hooks/useTrialStatus'

const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'))
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'))
const OrdersPage = lazy(() => import('@/pages/OrdersPage'))
const ClipsPage = lazy(() => import('@/pages/ClipsPage'))
const EditedContentPage = lazy(() => import('@/pages/EditedContentPage'))
const EditTemplatePage = lazy(() => import('@/pages/EditTemplatePage'))
const BrandKitsPage = lazy(() => import('@/pages/BrandKitsPage'))
const GroupsPage = lazy(() => import('@/pages/GroupsPage'))
const UsersPage = lazy(() => import('@/pages/UsersPage'))
const RewardsPage = lazy(() => import('@/pages/RewardsPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const BrandingPage = lazy(() => import('@/pages/BrandingPage'))
const PushPage = lazy(() => import('@/pages/PushPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const GlobalDashboardPage = lazy(() => import('@/pages/GlobalDashboardPage'))
const OutboundReviewPage = lazy(() => import('@/pages/outbound/OutboundReviewPage'))
const OutboundPipelinePage = lazy(() => import('@/pages/outbound/OutboundPipelinePage'))
const OutboundLeadsPage = lazy(() => import('@/pages/outbound/OutboundLeadsPage'))
const OutboundTemplatesPage = lazy(() => import('@/pages/outbound/OutboundTemplatesPage'))
const OutboundAnalyticsPage = lazy(() => import('@/pages/outbound/OutboundAnalyticsPage'))
const OutboundSettingsPage = lazy(() => import('@/pages/outbound/OutboundSettingsPage'))
const OutboundAuditLogPage = lazy(() => import('@/pages/outbound/OutboundAuditLogPage'))
const OutboundLeadDetailPage = lazy(() => import('@/pages/outbound/OutboundLeadDetailPage'))

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner />
    </div>
  )
}

// Guards still-in-progress features (not yet ready for customers) that are only reachable via
// sidebar links hidden from non-internal-admins — this blocks reaching them directly by URL too.
function InternalAdminOnlyRoute({ children }: { children: ReactElement }) {
  const isInternalAdmin = useIsInternalAdmin()
  if (!isInternalAdmin) return <Navigate to="/campaigns" replace />
  return children
}

export default function App() {
  useAuth()

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/clips" element={<ClipsPage />} />
          <Route path="/edited-content" element={<EditedContentPage />} />
          <Route
            path="/edit-template"
            element={
              <InternalAdminOnlyRoute>
                <EditTemplatePage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/branding" element={<BrandingPage />} />
          <Route
            path="/brand-kits"
            element={
              <InternalAdminOnlyRoute>
                <BrandKitsPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route path="/push" element={<PushPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/global" element={<GlobalDashboardPage />} />
          <Route
            path="/outbound/review"
            element={
              <InternalAdminOnlyRoute>
                <OutboundReviewPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/pipeline"
            element={
              <InternalAdminOnlyRoute>
                <OutboundPipelinePage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/leads"
            element={
              <InternalAdminOnlyRoute>
                <OutboundLeadsPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/templates"
            element={
              <InternalAdminOnlyRoute>
                <OutboundTemplatesPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/analytics"
            element={
              <InternalAdminOnlyRoute>
                <OutboundAnalyticsPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/settings"
            element={
              <InternalAdminOnlyRoute>
                <OutboundSettingsPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/audit-log"
            element={
              <InternalAdminOnlyRoute>
                <OutboundAuditLogPage />
              </InternalAdminOnlyRoute>
            }
          />
          <Route
            path="/outbound/leads/:id"
            element={
              <InternalAdminOnlyRoute>
                <OutboundLeadDetailPage />
              </InternalAdminOnlyRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/campaigns" replace />} />
      </Routes>
    </Suspense>
  )
}
